import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useCurrency } from "./CurrencyContext";

/**
 * Live-switchable site theme — US visitors ONLY.
 *
 * Indian visitors always see the unchanged Royal Amber experience. We
 * deliberately gate every theme switch on `country === "US"` so that no
 * matter what the admin flips, the Indian audience sees zero visual change.
 *
 * Mechanics:
 *  - On mount, fetch the active US theme from /api/themes/active-us
 *  - If the current visitor's country is "US" AND the theme is
 *    "cosmic_night", we add the `theme-cosmic-night` class to <html>
 *    — every other class in the app inherits Royal Amber styles by default,
 *    cosmic-night overrides via the `.theme-cosmic-night` namespace.
 *  - On every CurrencyContext.country change we re-evaluate (so flipping
 *    the manual currency override also flips the theme).
 *  - Polls every 60s for theme changes — when admin flips theme, all US
 *    sessions converge to the new theme within a minute without reload.
 */
const ThemeContext = createContext({
  themeId: "royal_amber",
  serverTheme: "royal_amber",
  isCosmicNight: false,
  isUsAudience: false,
});

export function ThemeProvider({ children }) {
  const { country } = useCurrency();
  const isUsAudience = country === "US";
  const [serverTheme, setServerTheme] = useState("royal_amber");

  // Fetch + poll for the active US theme. We only poll once we know we're
  // a US visitor, to avoid wasting requests for Indian visitors who would
  // never be affected anyway.
  useEffect(() => {
    if (!isUsAudience) return;
    let cancelled = false;
    const fetchTheme = () => {
      api.get("/themes/active-us")
        .then(({ data }) => { if (!cancelled) setServerTheme(data?.theme || "royal_amber"); })
        .catch(() => { /* keep last value */ });
    };
    fetchTheme();
    const id = setInterval(fetchTheme, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isUsAudience]);

  // Resolve the effective theme. Indian visitors always get royal_amber
  // regardless of what the server says — defence in depth.
  const themeId = isUsAudience ? serverTheme : "royal_amber";
  const isCosmicNight = themeId === "cosmic_night";

  // Add/remove the class on <html> so CSS overrides work site-wide.
  useEffect(() => {
    const html = document.documentElement;
    if (isCosmicNight) html.classList.add("theme-cosmic-night");
    else html.classList.remove("theme-cosmic-night");
    return () => { html.classList.remove("theme-cosmic-night"); };
  }, [isCosmicNight]);

  const value = useMemo(() => ({
    themeId,
    serverTheme,
    isCosmicNight,
    isUsAudience,
  }), [themeId, serverTheme, isCosmicNight, isUsAudience]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
